import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Users, Cookie, Mail, FileText } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#1e3a5f',
        color: 'white',
        padding: '2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Link to="/" style={{
            color: 'white',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '1rem'
          }}>
            ← Back to Home
          </Link>
          <h1 style={{
            margin: '0',
            fontSize: '2.5rem',
            fontWeight: '700',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <Shield size={40} />
            Privacy Policy
          </h1>
          <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>
            Last Updated: February 10, 2026
          </p>
        </div>
      </header>

      {/* Content */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '3rem 2rem'
      }}>
        {/* Introduction */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', margin: 0 }}>
            Estimate Assistant ("we," "our," or "us") is committed to protecting your privacy.
            This Privacy Policy explains how we collect, use, disclose, and safeguard your information
            when you use our construction estimating service. This policy is designed to comply with the
            General Data Protection Regulation (GDPR), California Consumer Privacy Act (CCPA), and other
            applicable privacy laws.
          </p>
        </section>

        {/* Information We Collect */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Database size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              1. Information We Collect
            </h2>
          </div>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            1.1 Information You Provide to Us
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Account Information:</strong> When you create an account through our authentication provider (Clerk), we collect your name, email address, and password (encrypted).</li>
            <li><strong>Company Profile Information:</strong> Company name, phone number, business address, license number, website, and company logo.</li>
            <li><strong>Project Data:</strong> Estimates, material lists, labor costs, project descriptions, photos you upload, and voice recordings you provide.</li>
            <li><strong>Payment Information:</strong> Payment details are collected and processed by our payment processor (Stripe). We do not store complete credit card numbers on our servers.</li>
            <li><strong>Communications:</strong> When you contact us for support, we collect the content of your messages and any attachments.</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            1.2 Information Collected Automatically
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Usage Data:</strong> Pages visited, features used, time spent on the service, and interaction patterns.</li>
            <li><strong>Device Information:</strong> Browser type, operating system, IP address, device identifiers, and general location data (city/country level).</li>
            <li><strong>Cookies and Similar Technologies:</strong> We use cookies, web beacons, and similar tracking technologies to enhance your experience. See Section 6 for details.</li>
          </ul>
        </section>

        {/* How We Use Your Information */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Eye size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              2. How We Use Your Information
            </h2>
          </div>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We use the information we collect to:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151' }}>
            <li>Provide, maintain, and improve our estimating service</li>
            <li>Create and manage your account</li>
            <li>Process your transactions and manage subscriptions</li>
            <li>Generate material pricing estimates and provide AI-powered analysis</li>
            <li>Store and sync your estimates across devices</li>
            <li>Send you service-related notifications and updates</li>
            <li>Respond to your comments, questions, and customer support requests</li>
            <li>Analyze usage patterns to improve our service</li>
            <li>Detect, prevent, and address technical issues and security threats</li>
            <li>Comply with legal obligations and enforce our Terms of Service</li>
            <li>Send marketing communications (with your consent, where required)</li>
          </ul>
        </section>

        {/* Legal Basis for Processing */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <FileText size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              3. Legal Basis for Processing (GDPR)
            </h2>
          </div>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            If you are located in the European Economic Area (EEA), our legal basis for processing your personal data includes:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151' }}>
            <li><strong>Contract Performance:</strong> Processing necessary to provide our service and fulfill our Terms of Service</li>
            <li><strong>Legitimate Interests:</strong> Operating, improving, and securing our service</li>
            <li><strong>Consent:</strong> Where you have provided consent for specific processing activities (e.g., marketing emails)</li>
            <li><strong>Legal Obligation:</strong> Compliance with applicable laws and regulations</li>
          </ul>
        </section>

        {/* Data Sharing and Disclosure */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Users size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              4. How We Share Your Information
            </h2>
          </div>

          <div style={{
            backgroundColor: '#dcfce7',
            border: '2px solid #86efac',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1.5rem'
          }}>
            <p style={{ margin: 0, fontWeight: '600', color: '#15803d' }}>
              ✓ We do not sell your personal data to third parties.
            </p>
          </div>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We may share your information in the following circumstances:
          </p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            4.1 Service Providers
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We share information with trusted third-party service providers who assist us in operating our service:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Clerk:</strong> Authentication and user management services</li>
            <li><strong>Stripe:</strong> Payment processing and subscription management</li>
            <li><strong>Cloud Infrastructure Providers:</strong> Hosting and data storage (Railway, Netlify)</li>
            <li><strong>AI Services:</strong> For photo analysis and voice transcription features</li>
            <li><strong>Analytics Providers:</strong> To understand service usage and improve user experience</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            4.2 Legal Requirements
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            We may disclose your information if required by law, court order, or government regulation, or if we believe disclosure is necessary to:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li>Comply with legal obligations</li>
            <li>Protect our rights, property, or safety</li>
            <li>Prevent fraud or illegal activity</li>
            <li>Protect the rights and safety of our users</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            4.3 Business Transfers
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change in ownership or control.
          </p>
        </section>

        {/* Data Storage and Security */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Lock size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              5. Data Storage and Security
            </h2>
          </div>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            5.1 Data Storage
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li>Your data is stored in secure cloud infrastructure within the United States</li>
            <li>Estimates and project data are stored in encrypted databases</li>
            <li>User authentication data is managed by Clerk with industry-standard security</li>
            <li>Payment information is stored and processed by Stripe in compliance with PCI DSS standards</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            5.2 Security Measures
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We implement appropriate technical and organizational measures to protect your personal data:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li>Encryption of data in transit (HTTPS/TLS)</li>
            <li>Encryption of data at rest</li>
            <li>Regular security audits and vulnerability assessments</li>
            <li>Access controls and authentication requirements</li>
            <li>Employee training on data protection and security</li>
            <li>Incident response procedures</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            5.3 Data Retention
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            We retain your personal data for as long as necessary to provide our service and comply with legal obligations. When you delete your account, we will delete or anonymize your personal data within 90 days, except where we are required to retain it for legal, tax, or regulatory purposes.
          </p>
        </section>

        {/* Cookies and Tracking */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Cookie size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              6. Cookies and Tracking Technologies
            </h2>
          </div>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We use cookies and similar tracking technologies to enhance your experience:
          </p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            Types of Cookies We Use:
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Essential Cookies:</strong> Required for authentication, security, and basic functionality</li>
            <li><strong>Performance Cookies:</strong> Help us understand how you use our service and improve performance</li>
            <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
            <li><strong>Analytics Cookies:</strong> Collect anonymous usage statistics</li>
          </ul>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            You can control cookies through your browser settings. However, disabling certain cookies may limit your ability to use some features of our service.
          </p>
        </section>

        {/* Your Rights */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Shield size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              7. Your Privacy Rights
            </h2>
          </div>

          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            Depending on your location, you may have the following rights regarding your personal data:
          </p>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            7.1 GDPR Rights (EEA Users)
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Right to Access:</strong> Request a copy of your personal data</li>
            <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Right to Erasure:</strong> Request deletion of your personal data ("right to be forgotten")</li>
            <li><strong>Right to Restriction:</strong> Request limitation of processing in certain circumstances</li>
            <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
            <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
            <li><strong>Right to Withdraw Consent:</strong> Withdraw consent at any time (where processing is based on consent)</li>
            <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            7.2 CCPA Rights (California Users)
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1.5rem' }}>
            <li><strong>Right to Know:</strong> Request information about the personal data we collect, use, and disclose</li>
            <li><strong>Right to Delete:</strong> Request deletion of your personal data</li>
            <li><strong>Right to Opt-Out:</strong> Opt-out of the "sale" of personal data (Note: We do not sell personal data)</li>
            <li><strong>Right to Non-Discrimination:</strong> Not be discriminated against for exercising your privacy rights</li>
          </ul>

          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#1e3a5f', marginTop: '1.5rem', marginBottom: '1rem' }}>
            7.3 How to Exercise Your Rights
          </h3>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            To exercise any of these rights, please contact us at:
          </p>
          <div style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: 0, color: '#1e3a5f', fontWeight: '600' }}>
              Email: privacy@estimateassistant.com
            </p>
          </div>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            We will respond to your request within 30 days (or as required by applicable law). We may need to verify your identity before processing your request.
          </p>
        </section>

        {/* Children's Privacy */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem' }}>
            8. Children's Privacy
          </h2>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            Our service is not intended for users under the age of 18. We do not knowingly collect personal
            information from children under 18. If you are a parent or guardian and believe your child has
            provided us with personal data, please contact us, and we will delete such information.
          </p>
        </section>

        {/* International Data Transfers */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem' }}>
            9. International Data Transfers
          </h2>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            Your information may be transferred to and processed in the United States or other countries where
            our service providers operate. These countries may have data protection laws that differ from your country.
          </p>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            For EEA users, we ensure appropriate safeguards are in place for international transfers, including
            Standard Contractual Clauses approved by the European Commission.
          </p>
        </section>

        {/* Third-Party Links */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem' }}>
            10. Third-Party Links
          </h2>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            Our service may contain links to third-party websites. We are not responsible for the privacy
            practices or content of these external sites. We encourage you to review the privacy policies
            of any third-party sites you visit.
          </p>
        </section>

        {/* Changes to Privacy Policy */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <h2 style={{ fontSize: '1.75rem', fontWeight: '600', color: '#1e293b', marginBottom: '1rem' }}>
            11. Changes to This Privacy Policy
          </h2>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            We may update this Privacy Policy from time to time to reflect changes in our practices or legal
            requirements. We will notify you of any material changes by:
          </p>
          <ul style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            <li>Posting the updated policy on our website with a new "Last Updated" date</li>
            <li>Sending you an email notification (for significant changes)</li>
            <li>Displaying a prominent notice on our service</li>
          </ul>
          <p style={{ lineHeight: '1.8', color: '#374151' }}>
            Your continued use of our service after changes are posted constitutes your acceptance of the updated policy.
          </p>
        </section>

        {/* Contact Information */}
        <section style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
            <Mail size={28} color="#1e3a5f" />
            <h2 style={{ margin: 0, fontSize: '1.75rem', fontWeight: '600', color: '#1e293b' }}>
              12. Contact Us
            </h2>
          </div>
          <p style={{ lineHeight: '1.8', color: '#374151', marginBottom: '1rem' }}>
            If you have questions, concerns, or requests regarding this Privacy Policy or our data practices,
            please contact us at:
          </p>
          <div style={{
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1rem'
          }}>
            <p style={{ margin: '0 0 0.5rem 0', color: '#1e293b', fontWeight: '600' }}>
              Estimate Assistant
            </p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#1e3a5f' }}>
              Email: privacy@estimateassistant.com
            </p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#1e3a5f' }}>
              Support: support@estimateassistant.com
            </p>
          </div>
          <p style={{ lineHeight: '1.8', color: '#374151', fontSize: '0.95rem' }}>
            For data protection inquiries from EEA users, you may also contact your local data protection authority.
          </p>
        </section>

        {/* Summary Box */}
        <section style={{
          backgroundColor: '#dbeafe',
          borderRadius: '12px',
          padding: '2rem',
          border: '2px solid #3b82f6'
        }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#1e3a5f', marginBottom: '1rem' }}>
            Quick Summary
          </h3>
          <ul style={{ lineHeight: '1.8', color: '#1e3a5f', marginBottom: 0 }}>
            <li>✓ We collect account info, estimates, and payment data to provide our service</li>
            <li>✓ We do not sell your personal data to third parties</li>
            <li>✓ Your data is encrypted and stored securely</li>
            <li>✓ You have rights to access, correct, and delete your data</li>
            <li>✓ We use trusted service providers (Clerk, Stripe) with strong security</li>
            <li>✓ You can contact us anytime about privacy concerns</li>
          </ul>
        </section>

        {/* Back to Top */}
        <div style={{ textAlign: 'center', marginTop: '3rem' }}>
          <Link
            to="/"
            style={{
              display: 'inline-block',
              backgroundColor: '#1e3a5f',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              transition: 'all 0.2s'
            }}
          >
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer style={{
        backgroundColor: '#1e3a5f',
        color: 'white',
        padding: '2rem',
        textAlign: 'center',
        marginTop: '4rem'
      }}>
        <p style={{ margin: '0 0 1rem 0' }}>
          © 2025 Estimate Assistant. All rights reserved.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2rem', flexWrap: 'wrap' }}>
          <Link to="/terms" style={{ color: 'white', textDecoration: 'underline' }}>
            Terms of Service
          </Link>
          <Link to="/privacy" style={{ color: 'white', textDecoration: 'underline' }}>
            Privacy Policy
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
